/**
 * Auto-scheduling around labor availability: the web screens.
 *
 * (Per-person caps, radius, skills and home base live in SchedulingProfileCard.)
 * - DispatchWeightsCard: the company's scoring weights (tunable without code).
 * - ReservedSlotConflicts: reserved weekly slots whose cleaner is now unavailable.
 * - DispatchProposalsPanel: the top three slot/cleaner options when booking a
 *   quote. Choosing one only fills the booking form; the dispatcher still books.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { dispatchApi } from '../../api/dispatch.ts';
import { Alert, Button, LoadingBlock } from '../ui.tsx';
import { formatLocalTime, formatNumericDate } from '../../i18n/datetime.ts';
import type { DispatchProposal, DispatchWeights } from '../../../shared/dispatch.ts';

const WEIGHT_KEYS = ['distance', 'skill', 'capacity', 'soonness'] as const;
const RULE_KEYS = ['requireSkillMatch', 'enforceDailyCap', 'enforceTravelRadius'] as const;

export function DispatchWeightsCard({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation();
  const queries = useQueryClient();
  const settings = useQuery({ queryKey: ['dispatch-settings'], queryFn: () => dispatchApi.settings() });
  const [weights, setWeights] = useState<DispatchWeights | null>(null);
  const [message, setMessage] = useState('');
  useEffect(() => {
    if (settings.data) setWeights(settings.data.weights);
  }, [settings.data]);
  const save = useMutation({
    mutationFn: (next: DispatchWeights) =>
      dispatchApi.saveSettings({ revision: settings.data ? settings.data.revision : 0, weights: next }),
    onSuccess: (saved) => {
      queries.setQueryData(['dispatch-settings'], saved);
      setMessage(t('dispatch.weights.saved'));
    },
    onError: (error: Error) => setMessage(error.message),
  });
  return (
    <section className="card mt-5 p-4" aria-label={t('dispatch.weights.title')}>
      <h2 className="text-lg font-semibold">{t('dispatch.weights.title')}</h2>
      <p className="mb-3 text-sm">{t('dispatch.weights.intro')}</p>
      {settings.isPending && <LoadingBlock />}
      {weights && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setMessage('');
            save.mutate(weights);
          }}
        >
          <div className="grid gap-3 sm:grid-cols-4">
            {WEIGHT_KEYS.map((key) => (
              <label key={key} className="block text-sm">
                <span className="field-label">{t(`dispatch.weights.${key}`)}</span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={100}
                  className="field-control min-h-11"
                  value={weights[key]}
                  disabled={!canEdit}
                  onChange={(event) =>
                    setWeights({ ...weights, [key]: Math.max(0, Math.min(100, Number(event.target.value) || 0)) })
                  }
                />
              </label>
            ))}
          </div>
          <div className="mt-3 flex flex-col gap-1">
            {RULE_KEYS.map((key) => (
              <label key={key} className="flex min-h-11 items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={weights[key]}
                  disabled={!canEdit}
                  onChange={(event) => setWeights({ ...weights, [key]: event.target.checked })}
                />
                {t(`dispatch.weights.${key}`)}
              </label>
            ))}
          </div>
          {canEdit && (
            <Button type="submit" variant="primary" className="mt-3" busy={save.isPending}>
              {t('dispatch.weights.save')}
            </Button>
          )}
          {message && (
            <p className="mt-2 text-sm" role="status">
              {message}
            </p>
          )}
        </form>
      )}
    </section>
  );
}

export function ReservedSlotConflicts({ from, to, userId }: { from: string; to: string; userId?: string }) {
  const { t } = useTranslation();
  const reservations = useQuery({
    queryKey: ['dispatch-reservations', from, to, userId],
    queryFn: () => dispatchApi.reservations({ from, to, userId, conflictsOnly: true }),
    retry: false,
  });
  return (
    <section className="card mb-5 p-4" aria-label={t('dispatch.reserved.title')}>
      <h2 className="mb-2 text-lg font-semibold">{t('dispatch.reserved.title')}</h2>
      <p className="mb-3 text-sm">{t('dispatch.reserved.intro')}</p>
      {reservations.isPending && <LoadingBlock />}
      {reservations.isError && <Alert tone="error">{t('dispatch.reserved.loadFailed')}</Alert>}
      {reservations.data && reservations.data.items.length === 0 && (
        <p className="text-sm text-[color:var(--color-muted)]">{t('dispatch.reserved.none')}</p>
      )}
      {reservations.data && reservations.data.items.length > 0 && (
        <ul className="divide-y">
          {reservations.data.items.map((item) => (
            <li key={item.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
              <span
                className="badge border-[color:var(--color-warning)] text-[color:var(--color-warning)]"
                data-conflict={item.conflict}
              >
                {t(`dispatch.conflict.${item.conflict}`)}
              </span>
              <span className="font-medium">{item.userName}</span>
              <span>{item.customerName}</span>
              <span className="text-[color:var(--color-muted)]">
                {new Date(item.startsAt).toLocaleString(undefined, {
                  timeZone: reservations.data.timezone,
                  dateStyle: 'medium',
                  timeStyle: 'short',
                })}
              </span>
              <Link className="ml-auto underline" to={`/schedule/series/${item.seriesId}`}>
                {t('dispatch.reserved.open')}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * The top three options for booking a quote. Choosing one fills the day, time
 * and cleaner in the booking form; nothing is booked or assigned until the
 * dispatcher confirms the dialog.
 */
export function DispatchProposalsPanel({
  quoteId,
  frequency,
  firstCleanOptionId,
  chosen,
  onChoose,
}: {
  quoteId: string;
  /** The cadence being booked, when it differs from the quote's own. */
  frequency?: string;
  /** The first-clean option about to be accepted, which sizes the visits. */
  firstCleanOptionId?: string;
  chosen: DispatchProposal | null;
  onChoose: (proposal: DispatchProposal | null) => void;
}) {
  const { t } = useTranslation();
  const proposals = useQuery({
    queryKey: ['dispatch-proposals', quoteId, frequency ?? null, firstCleanOptionId ?? null],
    queryFn: () => dispatchApi.proposals(quoteId, undefined, frequency, firstCleanOptionId),
    retry: false,
    staleTime: 30_000,
  });
  const isChosen = (p: DispatchProposal) =>
    chosen !== null &&
    chosen.userId === p.userId &&
    chosen.startsOn === p.startsOn &&
    chosen.startTimeLocal === p.startTimeLocal;
  return (
    <div className="mt-3" aria-label={t('dispatch.proposals.title')} role="group">
      <p className="field-label">{t('dispatch.proposals.title')}</p>
      {proposals.isPending && <LoadingBlock />}
      {proposals.isError && (
        <p className="text-sm text-[color:var(--color-muted)]">{t('dispatch.proposals.unavailable')}</p>
      )}
      {proposals.data && (proposals.data.requiredJobSkills?.length ?? 0) > 0 && (
        <p className="mb-2 text-sm text-[color:var(--color-muted)]">
          {t('autoSchedule.needsJob', {
            skills: proposals.data.requiredJobSkills.map((s) => t(`autoSchedule.skill.${s}`)).join(', '),
          })}
        </p>
      )}
      {proposals.data && proposals.data.proposals.length === 0 && (
        <p className="text-sm text-[color:var(--color-muted)]">
          {(proposals.data.crewSize ?? 1) > 1
            ? t('dispatch.proposals.crew', { count: proposals.data.crewSize })
            : t('dispatch.proposals.none')}
        </p>
      )}
      {proposals.data && proposals.data.proposals.length > 0 && (
        <ol className="grid gap-2">
          {proposals.data.proposals.map((p, index) => (
            <li key={`${p.userId}/${p.startsOn}/${p.startTimeLocal}`}>
              <button
                type="button"
                aria-pressed={isChosen(p)}
                onClick={() => onChoose(isChosen(p) ? null : p)}
                className={`card w-full min-h-11 p-3 text-left text-sm ${isChosen(p) ? 'ring-2 ring-[color:var(--color-primary)]' : ''}`}
              >
                <span className="flex flex-wrap items-baseline gap-2">
                  <span className="font-semibold">
                    {index + 1}. {p.userName}
                  </span>
                  <span>
                    {formatNumericDate(p.startsOn)} {formatLocalTime(p.startTimeLocal)}
                  </span>
                  <span className="ml-auto font-semibold">{t('dispatch.proposals.score', { score: p.score })}</span>
                </span>
                <span className="mt-1 block text-[color:var(--color-muted)]">
                  {[
                    p.distanceKm === null
                      ? t('dispatch.proposals.distanceUnknown')
                      : t('dispatch.proposals.distance', { km: p.distanceKm }),
                    p.maxJobsPerDay === null
                      ? t('dispatch.proposals.jobs', { count: p.jobsThatDay })
                      : t('dispatch.proposals.jobsOfCap', { count: p.jobsThatDay, cap: p.maxJobsPerDay }),
                    p.skillMatch ? t('dispatch.proposals.skillMatch') : t('dispatch.flag.skill_mismatch'),
                  ].join(' · ')}
                </span>
                {p.flags.filter((f) => f !== 'distance_unknown').length > 0 && (
                  <span className="mt-1 flex flex-wrap gap-1">
                    {p.flags
                      .filter((f) => f !== 'distance_unknown')
                      .map((f) => (
                        <span
                          key={f}
                          className="badge border-[color:var(--color-warning)] text-[color:var(--color-warning)]"
                        >
                          {f === 'missing_job_skill'
                            ? t('dispatch.flag.missing_job_skill', {
                                skills: (p.missingJobSkills ?? []).map((s) => t(`autoSchedule.skill.${s}`)).join(', '),
                              })
                            : t(`dispatch.flag.${f}`)}
                        </span>
                      ))}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ol>
      )}
      <p className="mt-1 text-xs text-[color:var(--color-muted)]">{t('dispatch.proposals.neverAuto')}</p>
    </div>
  );
}
